import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Sidebar from "@/components/dashboard/Sidebar";
import Header from "@/components/dashboard/Header";
import LiveTicker from "@/components/dashboard/LiveTicker";
import MapView from "@/components/dashboard/MapView";
import SentimentTree from "@/components/dashboard/SentimentTree";
import PostCard from "@/components/dashboard/PostCard";
import PostModal from "@/components/dashboard/PostModal";
import FilterBar from "@/components/dashboard/FilterBar";
import DrillPanel from "@/components/dashboard/DrillPanel";
import AreaDetailModal from "@/components/dashboard/AreaDetailModal";
import ChatWidget from "@/components/dashboard/ChatWidget";
import { SentimentLine, RiskBar, VolumeLine, CategoryDonut, AreaHBar, PALETTE } from "@/components/dashboard/charts";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { AlertTriangle, TrendingDown, Gauge, Hash, Globe, AlertCircle, RefreshCw } from "lucide-react";

// --- Ironclad: Global Error Boundary ---
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("Ironclad Failure:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-50 p-8 text-center">
          <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Intelligence Console Halted</h1>
          <p className="text-slate-500 max-w-md mb-8 text-sm leading-relaxed">
            A critical runtime error occurred in the visualization layer. The system core remains active.
          </p>
          <Button onClick={() => window.location.reload()} className="bg-[#020617] hover:bg-slate-800">
            <RefreshCw className="h-4 w-4 mr-2" /> Restore Console
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

function DashboardApp() {
  return (
    <ErrorBoundary>
      <DashboardContent />
    </ErrorBoundary>
  );
}

export default DashboardApp;

// Rename existing Dashboard to DashboardContent for wrapping
function DashboardContent() {
  const [active, setActive] = useState("overview");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [kpis, setKpis] = useState(null);
  const [hierarchy, setHierarchy] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [areaReport, setAreaReport] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [platformDist, setPlatformDist] = useState([]);
  const [activityDist, setActivityDist] = useState([]);
  const [severityDist, setSeverityDist] = useState([]);
  const [trendDay, setTrendDay] = useState([]);
  const [trendWeek, setTrendWeek] = useState([]);
  const [trendMonth, setTrendMonth] = useState([]);
  const [trendYear, setTrendYear] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [drillSelection, setDrillSelection] = useState(null);
  const [areaDetail, setAreaDetail] = useState(null);
  const [mapFocus, setMapFocus] = useState(null);
  const [wsStatus, setWsStatus] = useState("connecting");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [bucket, setBucket] = useState("day");
  const [trendsData, setTrendsData] = useState([]);

  // High-Performance Refs
  const routineAudioRef = useRef(null);
  const alertAudioRef = useRef(null);
  const refreshTimerRef = useRef(null);


  const jumpToMap = useCallback((post) => {
    if (!post?.location) return;
    setMapFocus({
      lat: post.location.lat,
      lng: post.location.lng,
      zoom: 11,
      label: post.site,
      sub: `${post.platform.toUpperCase()} · Risk ${post.risk_score}`,
      key: post.id,
    });
    setAreaDetail(null);
    setActive("map");
  }, []);

  const states = useMemo(() => [...new Set(markers.map((m) => m.state))].sort(), [markers]);
  const platforms = ["x", "instagram", "facebook", "linkedin", "reddit", "youtube"];

  // Optimized Global Data Hydra (KPIs + All Charting Aggregations)
  const [loading, setLoading] = useState(false);
  const refreshDashboardData = useCallback(async (signal = null) => {
    setLoading(true);
    try {
      const q = new URLSearchParams();

      // Enforce strict filter logic: Custom Range or Date Window
      if (filters.start_date || filters.end_date) {
        if (filters.start_date) q.set("start", filters.start_date);
        if (filters.end_date) q.set("end", filters.end_date);
      } else if (filters.date) {
        q.set("date", filters.date);
      } else {
        // Smart Window Fallback: Default Trends to 30d for meaningful history, others to 24h
        if (active === "trends") q.set("window", "30d");
        else q.set("window", "24h");
      }

      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all" && !["start_date", "end_date", "date", "date_range"].includes(k)) {
          q.set(k, v);
        }
      });

      const qs = q.toString();

      const [k, h, a, cats, pd, ad, sev, trend] = await Promise.all([
        api.get(`/kpis?${qs}`, { signal }),
        api.get(`/hierarchy?${qs}`, { signal }),
        api.get(`/reports/area?${qs}`, { signal }),
        api.get(`/reports/categories?${qs}`, { signal }),
        api.get(`/platform-distribution?${qs}`, { signal }),
        api.get(`/activity-distribution?${qs}`, { signal }),
        api.get(`/reports/severity?${qs}`, { signal }),
        api.get(`/sentiment-trend?bucket=${bucket}&${qs}`, { signal }),
      ]);

      setKpis(k.data);
      setHierarchy(h.data.tree);
      setAreaReport(a.data.items);
      setCategories(cats.data.categories);
      setVentures(cats.data.ventures);
      setPlatformDist(pd.data.items);
      setActivityDist(ad.data.items);
      setSeverityDist(sev.data.items);
      setTrendsData(trend.data.series);

      if (bucket === "day") {
        setTrendDay(trend.data.series);
      } else {
        const d = await api.get(`/sentiment-trend?bucket=day&${qs}`, { signal });
        setTrendDay(d.data.series);
      }

      setLastUpdated(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch (e) {
      if (e.name !== "CanceledError" && e.name !== "AbortError") console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, bucket, active]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Audio Lifecycle & Authorization
  useEffect(() => {
    // Pre-load assets into memory to eliminate network latency during alerts
    routineAudioRef.current = new Audio("https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3");
    alertAudioRef.current = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
    routineAudioRef.current.load();
    alertAudioRef.current.load();
  }, []);

  const handleToggleAudio = useCallback((val) => {
    setAudioEnabled(val);
    if (val) {
      // Authorization Prime: Play a silent burst to signal browser that this session is audio-active
      if (routineAudioRef.current) {
        routineAudioRef.current.volume = 0;
        routineAudioRef.current.play().catch(() => { });
      }
    }
  }, []);

  // Initial loads
  useEffect(() => {
    refreshDashboardData();
  }, [refreshDashboardData]);

  // filtered posts + markers
  useEffect(() => {
    const controller = new AbortController();

    const fetchPosts = async () => {
      try {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v && !["start_date", "end_date", "date"].includes(k)) q.set(k, v);
        });
        if (debouncedSearch) q.set("q", debouncedSearch);
        q.set("limit", "100");

        if (filters.start_date || filters.end_date) {
          if (filters.start_date) q.set("start", filters.start_date);
          if (filters.end_date) q.set("end", filters.end_date);
        } else if (filters.date) {
          q.set("date", filters.date);
        } else {
          q.set("window", "24h");
        }

        const r = await api.get(`/posts?${q}`, { signal: controller.signal });
        setPosts(r.data.items);
        setTotalPosts(r.data.total);
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") console.error("Post fetch error:", err);
      }
    };

    const fetchLocations = async () => {
      try {
        const q2 = new URLSearchParams();
        ["category", "severity", "state", "venture"].forEach((k) => { if (filters[k]) q2.set(k, filters[k]); });

        if (filters.start_date || filters.end_date) {
          if (filters.start_date) q2.set("start", filters.start_date);
          if (filters.end_date) q2.set("end", filters.end_date);
        } else if (filters.date) {
          q2.set("date", filters.date);
        } else {
          q2.set("window", "24h");
        }

        const r = await api.get(`/locations?${q2}`, { signal: controller.signal });
        setMarkers(r.data.markers);
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") console.error("Location fetch error:", err);
      }
    };

    fetchPosts();
    fetchLocations();

    return () => controller.abort();
  }, [filters, debouncedSearch]);

  // Strict Strategic Export: Only considers Platform + Date
  const handleExport = () => {
    const date = filters.date || new Date().toISOString().split("T")[0];
    const token = localStorage.getItem("cimap_token"); // Unified with Header logic
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

    const q = new URLSearchParams();
    q.set("date", date);
    if (token) q.set("access_token", token);

    // Per User Request: ONLY Platform and Date
    if (filters.platform && filters.platform !== "all") {
      q.set("platform", filters.platform);
    }

    const url = `${backendUrl}/api/posts/export?${q.toString()}`;
    window.open(url, "_blank");
  };

  // Real-time WebSocket bridge with auto-reconnect
  useEffect(() => {
    let reconnectTimer;
    let ws;

    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      // Smart detection: Priority 1: env var host, Priority 2: localhost detection, Priority 3: current host
      let backendHost = "";
      if (backendUrl) {
        backendHost = backendUrl.replace(/^http(s)?:\/\//, "");
      } else {
        backendHost = window.location.host === "localhost:3000" ? "localhost:8000" : window.location.host;
      }

      const wsUrl = `${protocol}//${backendHost}/ws/dashboard`;
      console.log("Connecting to WebSocket:", wsUrl);
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("WebSocket connected.");
        setWsStatus("connected");
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "NEW_POSTS") {
            const newPosts = msg.data;
            console.log(`📡 Ingested ${newPosts.length} fresh signals.`);
            // Update live feed immediately
            setPosts(prev => [...newPosts, ...prev].slice(0, 100));
            setTotalPosts(prev => prev + newPosts.length);

            // Audio Feedback (Cooing Fix): Use pre-authorized ref for zero-latency playback
            if (audioEnabled && routineAudioRef.current) {
              routineAudioRef.current.volume = 0.2;
              routineAudioRef.current.currentTime = 0;
              routineAudioRef.current.play().catch(e => console.warn("Routine audio blocked:", e));
            }

            // Throttled refresh: Prevent UI lockup during high-volume bursts
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => {
              refreshDashboardData();
            }, 250); // 250ms debounce
          } else if (msg.type === "HIGH_RISK_ALERT" || msg.type === "MULTI_HIGH_RISK_ALERT") {
            console.log(`🚨 CRITICAL SIGNAL DETECTED:`, msg);
            // Strategic Audio Alert: Immediate high-priority siren
            if (audioEnabled && alertAudioRef.current) {
              alertAudioRef.current.volume = 0.5;
              alertAudioRef.current.currentTime = 0;
              alertAudioRef.current.play().catch(e => console.warn("Siren blocked:", e));
            }

            if (msg.type === "HIGH_RISK_ALERT") {
              const post = msg.data;
              toast.custom((t) => (
                <div className={`p-4 rounded-lg glass-alert pulse-red shadow-2xl w-[380px] cursor-pointer`} onClick={() => { jumpToMap(post); toast.dismiss(t); }}>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-1" />
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] font-bold text-red-500 tracking-widest uppercase">Critical Intel</span>
                        <span className="text-[10px] text-white/40">Real-time</span>
                      </div>
                      <h4 className="text-sm font-semibold text-white mb-2">{post.title || post.content.substring(0, 50)}...</h4>
                      <div className="bg-red-500/20 border border-red-500/30 text-[9px] font-bold text-red-400 px-2 py-0.5 rounded inline-block">RISK {post.risk_score}</div>
                    </div>
                  </div>
                </div>
              ), { duration: 10000 });
            } else {
              toast.success(`Strategic Alert: ${msg.summary}`, { duration: 8000 });
            }
          }
        } catch (err) { console.error("WS Message Error:", err); }
      };

      ws.onclose = () => {
        setWsStatus("disconnected");
        console.log("WebSocket closed. Reconnecting in 5s...");
        reconnectTimer = setTimeout(connect, 5000);
      };

      ws.onerror = (e) => {
        console.error("WebSocket error:", e);
        ws.close();
      };
    };

    connect();
    return () => {
      if (ws) ws.close();
      clearTimeout(reconnectTimer);
    };
  }, [audioEnabled, refreshDashboardData, jumpToMap]);

  const filteredPosts = useMemo(() => {
    if (!search) return posts;
    const s = search.toLowerCase();
    return posts.filter((p) =>
      (p.content?.toLowerCase() || "").includes(s) ||
      (p.author?.toLowerCase() || "").includes(s) ||
      (p.site?.toLowerCase() || "").includes(s) ||
      (p.handle?.toLowerCase() || "").includes(s) ||
      (p.platform?.toLowerCase() || "").includes(s) ||
      (p.activity_category?.toLowerCase() || "").includes(s)
    );
  }, [posts, search]);

  // High-Performance Long-Polling Refresher
  useEffect(() => {
    const controller = new AbortController();
    refreshDashboardData(controller.signal);

    // Efficient Polling: 30s intervals
    const interval = setInterval(() => {
      refreshDashboardData();
    }, 30000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [refreshDashboardData]);

  // View-Specific Contextual Fetching
  useEffect(() => {
    const controller = new AbortController();
    // Fetch deeper context if we are in specialized views
    if (active === "feed" || active === "map" || active === "area") {
      // These are largely handled by the global refresh, but we could add specific drills here
    }
    return () => controller.abort();
  }, [active]);

  return (
    <div className="h-screen w-screen flex flex-col bg-[#F8FAFC] overflow-hidden" data-testid="dashboard-page">
      <LiveTicker onPostClick={setSelectedPost} />
      <SeverityBar
        data={severityDist}
        onClick={(sev) => { setFilters({ ...filters, severity: sev }); setActive("feed"); }}
        collapsed={sidebarCollapsed}
      />

      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          active={active}
          onChange={setActive}
          kpis={kpis}
          collapsed={sidebarCollapsed}
        />

        <main className="flex-1 flex flex-col overflow-hidden">
          <Header
            onSearch={setSearch}
            lastUpdated={lastUpdated}
            currentPlatform={filters.platform}
            onExport={handleExport}
            wsStatus={wsStatus}
            audioEnabled={audioEnabled}
            setAudioEnabled={handleToggleAudio}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
          />

          <FilterBar
            filters={filters}
            setFilters={setFilters}
            categories={categories}
            ventures={ventures}
            states={states}
            platforms={platforms}
          />

          <div className="flex-1 overflow-auto thin-scroll">
            {active === "overview" && (
              <>
                <OverviewView
                  kpis={kpis}
                  markers={markers}
                  hierarchy={hierarchy}
                  posts={filteredPosts}
                  totalPosts={totalPosts}
                  activityDist={activityDist}
                  trendDay={trendDay}
                  platformDist={platformDist}
                  severityDist={severityDist}
                  onMarkerClick={(m) => setAreaDetail(m.state)}
                  onPost={(p) => setSelectedPost(p)}
                  onJumpToMap={jumpToMap}
                  onTreeSelect={(sel) => setDrillSelection(sel)}
                  onPlatformClick={(p) => { setFilters({ ...filters, platform: p }); setActive("feed"); }}
                  onKpiClick={(k) => {
                    if (k === "high") { setFilters({ ...filters, severity: "critical" }); setActive("feed"); }
                    else if (k === "critical") { setFilters({ ...filters, severity: "critical" }); setActive("feed"); }
                    else if (k === "sites") { setActive("map"); }
                    else if (k === "total" || k === "sentiment") { setActive("feed"); }
                  }}
                />
                {drillSelection && (
                  <DrillPanel selection={drillSelection} onClose={() => setDrillSelection(null)} onOpenPost={setSelectedPost} onJumpToMap={jumpToMap} />
                )}
              </>
            )}
            {active === "map" && (
              <MapStandalone markers={markers} onMarkerClick={(m) => setAreaDetail(m.state)} focus={mapFocus} onClearFocus={() => setMapFocus(null)} />
            )}
            {active === "tree" && (
              <div className="p-4 space-y-4">
                <div className="border border-slate-200 bg-white">
                  <div className="px-5 py-3 border-b border-slate-200">
                    <div className="kicker">Drill-down</div>
                    <h3 className="font-display font-semibold">Business Venture → Site → Category → Subpoint</h3>
                    <p className="text-xs text-slate-500 mt-1">Click any node to surface its signals in the panel below.</p>
                  </div>
                  <div className="h-[520px]">
                    <SentimentTree tree={hierarchy} onSelect={(sel) => setDrillSelection(sel)} />
                  </div>
                </div>
                {drillSelection && (
                  <DrillPanel selection={drillSelection} onClose={() => setDrillSelection(null)} onOpenPost={setSelectedPost} onJumpToMap={jumpToMap} />
                )}
              </div>
            )}
            {active === "feed" && (
              <FeedView posts={filteredPosts} total={totalPosts} onPost={setSelectedPost} onJumpToMap={jumpToMap} />
            )}
            {active === "trends" && (
              <TrendsView
                bucket={bucket}
                setBucket={setBucket}
                series={trendsData}
                filters={filters}
                onFilterChange={setFilters}
                loading={loading}
              />
            )}
            {active === "area" && (
              <AreaReport
                data={areaReport}
                onRowClick={(state) => setAreaDetail(state)}
              />
            )}

            {active === "platforms" && (
              <PlatformView
                data={platformDist}
                onClick={(platform) => { setFilters({ ...filters, platform }); setActive("feed"); }}
              />
            )}
          </div>
        </main>
      </div>

      <PostModal post={selectedPost} onClose={() => setSelectedPost(null)} onJumpToMap={jumpToMap} />
      <AreaDetailModal open={!!areaDetail} state={areaDetail} onClose={() => setAreaDetail(null)} onOpenPost={setSelectedPost} onJumpToMap={jumpToMap} />
      <ChatWidget onJumpToMap={jumpToMap} />
    </div>
  );
}

/* ---------------- Views ---------------- */

function Panel({ title, kicker, right, children, className = "", testId }) {
  return (
    <section className={`border border-slate-200 bg-white ${className}`} data-testid={testId}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <div className="kicker">{kicker}</div>
          <h3 className="font-display text-[15px] font-semibold tracking-tight">{title}</h3>
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

function KPICard({ label, value, sub, tone = "default", icon: Icon, onClick, testId }) {
  const colors = {
    critical: "text-[#dc2626] border-l-[#dc2626]",
    warning: "text-[#ea580c] border-l-[#ea580c]",
    info: "text-[#0284c7] border-l-[#0284c7]",
    success: "text-[#059669] border-l-[#059669]",
    default: "text-slate-900 border-l-slate-900",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`text-left bg-white border border-slate-200 border-l-4 ${colors} p-4 transition-all ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300" : "cursor-default"
        }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="kicker">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1 font-mono">{sub}</div>}
      {onClick && <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mt-2">View details →</div>}
    </button>
  );
}

function OverviewView({ kpis, markers, hierarchy, posts, totalPosts, activityDist, trendDay, platformDist, severityDist, onMarkerClick, onPost, onPlatformClick, onTreeSelect, onKpiClick, onJumpToMap }) {
  const total = platformDist.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="p-4 space-y-4">
      {/* 1. Core KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Total Signals" value={kpis?.total_posts} sub="All platforms · last 60d" icon={Hash} onClick={() => onKpiClick("total")} testId="kpi-total" />
        <KPICard label="High-Risk Signals" value={kpis?.high_risk_count} sub={`${kpis?.critical_count ?? 0} critical`} tone="critical" icon={AlertTriangle} onClick={() => onKpiClick("high")} testId="kpi-high" />
        <KPICard label="Avg Sentiment" value={kpis?.avg_sentiment} sub="-1 very negative · +1 positive" tone="info" icon={TrendingDown} onClick={() => onKpiClick("sentiment")} testId="kpi-sentiment" />
        <KPICard label="Active Sites" value={kpis?.active_sites} sub={`${kpis?.posts_last_24h ?? 0} signals in 24h`} tone="success" icon={Gauge} onClick={() => onKpiClick("sites")} testId="kpi-sites" />
      </div>

      {/* 2. Platform Vertical Distribution */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {["twitter", "linkedin", "facebook", "youtube", "reddit", "instagram"].map(p => {
          const stats = platformDist.find(d => d.platform === (p === "twitter" ? "twitter" : p) || (p === "twitter" && d.platform === "x"));
          return (
            <KPICard
              key={p}
              label={p === "x" || p === "twitter" ? "Twitter" : p.charAt(0).toUpperCase() + p.slice(1)}
              value={stats?.count ?? 0}
              sub={`${stats ? ((stats.count / total) * 100).toFixed(1) : 0}% share`}
              tone="info"
              onClick={() => onPlatformClick && onPlatformClick(p)}
              testId={`overview-platform-${p}`}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel title="Geospatial Exposure" kicker="Map view" className="col-span-12 lg:col-span-8 h-[420px] flex flex-col" testId="overview-map-panel">
          <div className="flex-1"><MapView markers={markers} onMarkerClick={onMarkerClick} /></div>
        </Panel>

        <Panel title="Sentiment Drill-Down" kicker="Venture → Site → Activity" className="col-span-12 lg:col-span-4 h-[420px] flex flex-col" testId="overview-tree-panel">
          <div className="flex-1 overflow-hidden"><SentimentTree tree={hierarchy} onSelect={onTreeSelect} /></div>
        </Panel>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel title="Sentiment Trajectory" kicker="Daily (last 30d)" className="col-span-12 lg:col-span-6">
          <div className="p-4"><SentimentLine data={trendDay.slice(-30)} /></div>
        </Panel>
        <Panel title="Risk Intensity" kicker="Daily avg risk" className="col-span-12 lg:col-span-6">
          <div className="p-4"><RiskBar data={trendDay.slice(-30)} /></div>
        </Panel>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel title="Activity Categories" kicker="Distribution · click to drill" className="col-span-12 lg:col-span-5">
          <div className="p-4 grid grid-cols-2 gap-2">
            <CategoryDonut data={activityDist} />
            <div className="text-[11px] space-y-1 font-mono overflow-y-auto max-h-[220px] thin-scroll">
              {activityDist.map((a, i) => (
                <button
                  key={a.category}
                  onClick={() => onTreeSelect({ category: a.category })}
                  className="flex items-center gap-2 w-full border-b border-slate-100 py-1 hover:bg-slate-50"
                  data-testid={`category-row-${i}`}
                >
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                  <span className="truncate flex-1 text-slate-600 text-left">{a.category}</span>
                  <span className="font-semibold text-slate-900">{a.count}</span>
                </button>
              ))}
            </div>
          </div>
        </Panel>

        <Panel title="Signal Volume" kicker="Posts per day" className="col-span-12 lg:col-span-7">
          <div className="p-4"><VolumeLine data={trendDay.slice(-30)} /></div>
        </Panel>
      </div>

      <Panel title="Post Feed" kicker={`${totalPosts} signals · filtered`} testId="overview-feed" right={
        <div className="text-[11px] font-mono text-slate-500">{posts.length} shown</div>
      }>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="feed-grid">
          {posts.slice(0, 12).map((p) => <PostCard key={p.id} post={p} onClick={onPost} onJumpToMap={onJumpToMap} />)}
        </div>
      </Panel>
    </div>
  );
}

function MapStandalone({ markers, onMarkerClick, focus, onClearFocus }) {
  return (
    <div className="p-4 h-full">
      <div className="border border-slate-200 bg-white h-full flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="kicker">Map view</div>
            <h3 className="font-display font-semibold">Geospatial Intelligence</h3>
            <p className="text-xs text-slate-500 mt-1">Click any marker to open the regional detail view.</p>
          </div>
          <div className="flex items-center gap-3">
            {focus && (
              <button
                onClick={onClearFocus}
                className="text-[11px] uppercase tracking-[0.18em] text-slate-600 border border-slate-300 px-3 py-1.5 rounded-sm hover:bg-slate-50"
                data-testid="clear-map-focus"
              >
                Clear focus
              </button>
            )}
            <div className="text-[11px] font-mono text-slate-500">{markers.length} sites · interactive</div>
          </div>
        </div>
        <div className="flex-1"><MapView markers={markers} onMarkerClick={onMarkerClick} focus={focus} /></div>
      </div>
    </div>
  );
}

function FeedView({ posts, total, onPost, onJumpToMap }) {
  return (
    <div className="p-4">
      <Panel title="Live Post Feed" kicker={`${total} signals`} right={<div className="text-[11px] font-mono text-slate-500">{posts.length} shown</div>}>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {posts.map((p) => <PostCard key={p.id} post={p} onClick={onPost} onJumpToMap={onJumpToMap} />)}
        </div>
      </Panel>
    </div>
  );
}

function TrendsView({ bucket, setBucket, series, filters, onFilterChange, loading }) {
  const buckets = [
    { id: "hour", label: "Hourly (24h)" },
    { id: "day", label: "Daily" },
    { id: "week", label: "Weekly" },
    { id: "month", label: "Monthly" },
    { id: "year", label: "Yearly" }
  ];

  const colors = { hour: "bg-emerald-600", day: "bg-blue-600", week: "bg-indigo-600", month: "bg-purple-600", year: "bg-slate-900" };
  const hasRange = !!(filters.start_date || filters.end_date);

  // Auto-switch to daily if window is small, or monthly if window is large
  // But respect user's explicit selection

  return (
    <div className={`p-4 space-y-4 transition-opacity duration-300 ${loading ? "opacity-60 pointer-events-none" : "opacity-100"}`}>
      <div className="bg-white border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-8 shadow-sm relative overflow-hidden">
        {loading && <div className="absolute inset-0 bg-white/40 flex items-center justify-center z-10"><div className="h-1 w-full bg-blue-600/20 absolute top-0 overflow-hidden"><div className="h-full bg-blue-600 animate-progress w-1/3" /></div></div>}

        <div className="flex items-center gap-8">
          <div className="shrink-0">
            <div className="kicker mb-2">Aggregation Precision</div>
            <div className="flex bg-slate-100 p-1 rounded-sm gap-1">
              {buckets.map(b => (
                <button
                  key={b.id}
                  onClick={() => {
                    setBucket(b.id);
                    // If they select a standard bucket, we can optionally clear custom range
                    // but it's better to keep it if they explicitly set it.
                  }}
                  className={`px-5 py-1.5 text-xs font-bold rounded-sm transition-all ${bucket === b.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <div className="h-12 w-[1px] bg-slate-100" />

          <div className="flex items-center gap-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors ${hasRange ? "bg-blue-50 border border-blue-200 shadow-inner" : "bg-slate-50 border border-slate-100"}`}>
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${hasRange ? "bg-blue-600 animate-pulse" : "bg-slate-300"}`} />
                <div className={`text-[10px] font-bold uppercase tracking-wider ${hasRange ? "text-blue-600" : "text-slate-400"}`}>
                  {hasRange ? "Custom Analytical Range" : "Default (24h Rolling Window)"}
                </div>
              </div>
              <div className="h-4 w-[1px] bg-slate-200 mx-2" />
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase text-slate-400 font-bold mb-0.5">Start</span>
                  <input
                    type="date"
                    value={filters.start_date || ""}
                    onChange={(e) => onFilterChange({ ...filters, start_date: e.target.value, date: "" })}
                    className="bg-transparent border-none p-0 text-xs font-mono focus:ring-0 w-[110px]"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] uppercase text-slate-400 font-bold mb-0.5">End</span>
                  <input
                    type="date"
                    value={filters.end_date || ""}
                    onChange={(e) => onFilterChange({ ...filters, end_date: e.target.value, date: "" })}
                    className="bg-transparent border-none p-0 text-xs font-mono focus:ring-0 w-[110px]"
                  />
                </div>
              </div>
            </div>

            {hasRange && (
              <button
                className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-tight flex items-center gap-1.5 px-3 border border-red-100 rounded-sm py-2 hover:bg-red-50 transition-colors"
                onClick={() => onFilterChange({ ...filters, start_date: "", end_date: "" })}
              >
                Clear Range
              </button>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Data Source</div>
          <div className="text-xs font-bold text-slate-900">Historical Archive</div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel title="Volume Intensity" kicker="Total signals in window" className="col-span-12 h-[320px] flex flex-col">
          <div className="flex-1 p-4"><VolumeLine data={series} /></div>
        </Panel>
        <Panel title="Sentiment Trajectory" kicker="Avg sentiment index" className="col-span-12 lg:col-span-6 h-[300px] flex flex-col">
          <div className="flex-1 p-4"><SentimentLine data={series} /></div>
        </Panel>
        <Panel title="Risk Index Evolution" kicker="Weighted risk score" className="col-span-12 lg:col-span-6 h-[300px] flex flex-col">
          <div className="flex-1 p-4"><RiskBar data={series} /></div>
        </Panel>
      </div>

      <Panel title="Aggregated Historical Data" kicker={`${series.length} data points aggregated`}>
        <div className="p-0 overflow-auto max-h-[400px]">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 sticky top-0 z-20">
              <tr>
                <th className="text-left px-6 py-3 font-bold uppercase tracking-wider">Time Period</th>
                <th className="text-right px-6 py-3 font-bold uppercase tracking-wider">Signal Volume</th>
                <th className="text-right px-6 py-3 font-bold uppercase tracking-wider">Avg Sentiment</th>
                <th className="text-right px-6 py-3 font-bold uppercase tracking-wider">Avg Risk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {series.length === 0 ? (
                <tr><td colSpan="4" className="py-20 text-center text-slate-400 italic">No data found for the selected range/filters.</td></tr>
              ) : (
                series.map((s, i) => (
                  <tr key={s.bucket} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-2.5 font-semibold text-slate-900">{s.bucket}</td>
                    <td className="text-right px-6 py-2.5">
                      <div className="flex items-center justify-end gap-3">
                        <span className="font-bold">{s.posts.toLocaleString()}</span>
                        <div className="h-1.5 w-12 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${colors[bucket]}`} style={{ width: `${Math.min(100, (s.posts / (Math.max(...series.map(x => x.posts)) || 1)) * 100)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className={`text-right px-6 py-2.5 font-bold ${s.avg_sentiment < 0 ? "text-red-600" : s.avg_sentiment > 0.1 ? "text-emerald-600" : "text-slate-400"}`}>
                      {s.avg_sentiment > 0 ? `+${s.avg_sentiment}` : s.avg_sentiment}
                    </td>
                    <td className="text-right px-6 py-2.5 font-bold text-slate-700">{s.avg_risk}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function TimeReport() { return null; } // Legacy fallback

function AreaReport({ data, onRowClick }) {
  return (
    <div className="p-4 space-y-4">
      <Panel title="Area Impact Report" kicker="State-wise signal count">
        <div className="p-4"><AreaHBar data={data} /></div>
      </Panel>
      <Panel title="State Summary" kicker={`${data.length} regions · click a row for details`}>
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200 uppercase tracking-[0.18em] text-[10px]">
              <tr><th className="text-left py-2">State</th><th className="text-right">Signals</th><th className="text-right">Critical</th><th className="text-right">Avg Risk</th><th className="text-right">Avg Sentiment</th><th></th></tr>
            </thead>
            <tbody className="font-mono">
              {data.map((r) => (
                <tr
                  key={r.state}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => onRowClick && onRowClick(r.state)}
                  data-testid={`area-row-${r.state.replace(/\s+/g, "-")}`}
                >
                  <td className="py-2 font-sans">{r.state}</td>
                  <td className="text-right">{r.count}</td>
                  <td className="text-right text-[#dc2626] font-semibold">{r.critical}</td>
                  <td className="text-right">{r.avg_risk}</td>
                  <td className="text-right">{r.avg_sentiment}</td>
                  <td className="text-right text-slate-400 font-sans text-[11px]">view →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}



function PlatformView({ data, onClick }) {
  const total = data.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {data.map((r) => (
          <KPICard
            key={r.platform}
            label={r.platform}
            value={r.count}
            sub={`${((r.count / total) * 100).toFixed(1)}%`}
            onClick={() => onClick && onClick(r.platform)}
            testId={`platform-card-${r.platform}`}
          />
        ))}
      </div>
      <Panel title="Platform Share" kicker="By signal volume · click a card to drill in">
        <div className="p-4 grid grid-cols-2 gap-4">
          <CategoryDonut data={data.map((r) => ({ category: r.platform, count: r.count }))} />
          <div className="text-xs font-mono space-y-2 self-center">
            {data.map((r, i) => (
              <button
                key={r.platform}
                onClick={() => onClick && onClick(r.platform)}
                className="flex items-center gap-2 w-full hover:bg-slate-50 p-1 rounded-sm"
              >
                <span className="h-2 w-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                <span className="capitalize flex-1 text-left">{r.platform}</span>
                <span className="font-semibold">{r.count}</span>
                <span className="text-slate-400">({((r.count / total) * 100).toFixed(1)}%)</span>
              </button>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}

function SeverityBar({ data, onClick }) {
  const order = ["critical", "high", "medium", "low"];
  const sorted = [...data].sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity));
  const colors = { critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#059669" };
  const total = sorted.reduce((s, r) => s + r.count, 0) || 1;

  return (
    <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center gap-8 h-12 shrink-0">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 shrink-0">Severity Mix</div>
      <div className="flex-1 h-3 flex rounded-full overflow-hidden bg-slate-100">
        {sorted.map((r) => {
          const pct = (r.count / total) * 100;
          return (
            <button
              key={r.severity}
              onClick={() => onClick && onClick(r.severity)}
              style={{ width: `${pct}%`, background: colors[r.severity] }}
              className="h-full flex items-center justify-center text-[9px] text-white font-bold hover:opacity-90 transition-opacity"
              title={`${r.severity}: ${r.count} (${pct.toFixed(1)}%)`}
            >
              {pct > 2 ? `${pct.toFixed(1)}%` : ""}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-5 shrink-0">
        {sorted.map((r) => {
          const pct = (r.count / total) * 100;
          return (
            <button
              key={r.severity}
              onClick={() => onClick && onClick(r.severity)}
              className="flex items-center gap-2 hover:opacity-70 transition-opacity"
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: colors[r.severity] }} />
              <span className="text-[11px] font-mono font-semibold text-slate-700 capitalize">
                {r.severity}: {r.count} <span className="text-slate-400 font-normal">({pct.toFixed(1)}%)</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
